/** The emailed-report merge preview (GET /api/email-source-duplicates), as the server sends it. */
export interface MergeMember {
  id: string;
  label: string | null;
  providerLabel: string | null;
  enabled: boolean;
  createdAt: string;
  days: number;
  firstDay: string | null;
  lastDay: string | null;
  rows: number;
  /** links that are not rejected: the ones that can move money */
  links: number;
  payoutNow: Record<string, string>;
  payoutKept: Record<string, string>;
}

export interface MergeGroup {
  target: string;
  targetExists: boolean;
  members: MergeMember[];
  overlapDays: number;
  rowsDropped: number;
  payoutNow: Record<string, string>;
  payoutAfter: Record<string, string>;
  conflicts: string[];
  linksDropped: number;
  linksMoved: number;
}

/** A 4-place amount from the server as cents, rounded half up, never through a float. */
export function moneyText(amount: string, currency: string): string {
  const m = /^(-?)(\d+)(?:\.(\d{1,4}))?$/.exec(amount);
  if (!m) return `${amount} ${currency}`;
  const frac = (m[3] ?? '').padEnd(4, '0');
  let cents = BigInt(m[2]!) * 100n + BigInt(frac.slice(0, 2));
  if (frac.charCodeAt(2) >= 53) cents += 1n; // third decimal 5-9 rounds up
  const whole = (cents / 100n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const sign = m[1] && cents !== 0n ? '-' : '';
  const symbol = currency === 'USD' ? '$' : '';
  return `${sign}${symbol}${whole}.${(cents % 100n).toString().padStart(2, '0')}${symbol ? '' : ` ${currency}`}`;
}

/** "$58.50", or "$58.50 + 10.00 EUR" for several currencies; "nothing" when empty. */
export function moneyMap(m: Record<string, string>): string {
  const parts = Object.entries(m).map(([cur, v]) => moneyText(v, cur));
  return parts.length ? parts.join(' + ') : 'nothing';
}

/** What merging does to the money, in one sentence for someone who is not reading code. */
export function effectText(g: MergeGroup): string {
  if (!g.overlapDays) return 'No day is held by two of them, so no figure changes.';
  const days = `${g.overlapDays} day${g.overlapDays === 1 ? ' is' : 's are'} held by more than one of them`;
  return `${days}: each is counted once, from the newest email. Revenue on this report goes from ${moneyMap(g.payoutNow)} to ${moneyMap(g.payoutAfter)}; the ${g.rowsDropped} older row${g.rowsDropped === 1 ? '' : 's'} set aside are kept in history, not deleted.`;
}
